import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import FormChuVuon from "components/FormChuVuon";
import { oTieuDeCot, AN_DUOI_MD, chuXuongDong } from "lib/kieuBangDuLieu";
import {
  useDialogFullManHinh,
  oDialogContentFormDai,
  oDialogActionsFormDai,
  oDialogPaperFormDai,
  oDialogFormFlex,
} from "lib/dungDialogFullManHinh";
import { chuVuonApi } from "api/chuVuon";
import { giaoDichApi } from "api/giaoDich";
import { chiFieldThayDoi, yeuCauSuaChuVuonVuonApi } from "api/yeuCauSuaChuVuonVuon";
import QrCodeImage from "components/QrCodeImage";
import { NHAN_LOAI_CHU_VUON } from "lib/quyTacMaChuVuon";
import { hienThiCccd, soCccdDayDuTuHoSo } from "lib/cccdBaoMat";
import { apDungNoiCapCccdTheoNgay } from "lib/thongTinCaNhanChuVuon";
import { useAuth } from "context/AuthContext";
import useTour from "hooks/useTour";
import buocChuVuonChiTiet from "tours/chuVuonChiTiet";

function Dong({ nhan, giaTri }) {
  return (
    <Grid item xs={12} sm={6}>
      <MDTypography variant="overline" color="text" lineHeight={1}>
        {nhan}
      </MDTypography>
      <MDTypography
        variant="button"
        fontWeight="medium"
        display="block"
        mt={0.25}
        sx={chuXuongDong}
      >
        {giaTri || "—"}
      </MDTypography>
    </Grid>
  );
}

Dong.propTypes = {
  nhan: PropTypes.string.isRequired,
  giaTri: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Dong.defaultProps = {
  giaTri: null,
};

function ngayHienThi(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("vi-VN");
}

function ChiTietChuVuon() {
  const { id } = useParams();
  const { vaiTro } = useAuth();
  const laAdmin = vaiTro === "admin";
  const fullManHinh = useDialogFullManHinh();
  const [chuVuon, setChuVuon] = useState(null);
  const [danhSachPO, setDanhSachPO] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [modalDeXuat, setModalDeXuat] = useState(null);
  const [dangGuiDeXuat, setDangGuiDeXuat] = useState(false);
  const [loiDeXuat, setLoiDeXuat] = useState("");
  const [thongBaoDeXuat, setThongBaoDeXuat] = useState("");

  useEffect(() => {
    setDangTai(true);
    let cvXong = false;
    let gdXong = false;
    const danhDauXong = () => {
      if (cvXong && gdXong) setDangTai(false);
    };

    const huyCv = chuVuonApi.langNgheChiTiet(
      id,
      (cv) => {
        setChuVuon(cv);
        cvXong = true;
        danhDauXong();
      },
      () => {
        cvXong = true;
        danhDauXong();
      }
    );
    const huyGd = giaoDichApi.langNgheTheoChuVuon(
      id,
      (gd) => {
        setDanhSachPO(gd);
        gdXong = true;
        danhDauXong();
      },
      () => {
        gdXong = true;
        danhDauXong();
      }
    );
    return () => {
      huyCv();
      huyGd();
    };
  }, [id]);

  useTour("chu-vuon-chi-tiet", dangTai || !chuVuon ? [] : buocChuVuonChiTiet());

  function moDeXuatSua() {
    setLoiDeXuat("");
    setThongBaoDeXuat("");
    setModalDeXuat({ ...chuVuon });
  }

  async function guiDeXuatChuVuon(e) {
    e.preventDefault();
    setDangGuiDeXuat(true);
    setLoiDeXuat("");
    setThongBaoDeXuat("");
    try {
      const daAp = apDungNoiCapCccdTheoNgay(modalDeXuat);
      const patch = chiFieldThayDoi(chuVuon, daAp);
      if (Object.keys(patch).length === 0) {
        throw new Error("Không có thay đổi nào để gửi duyệt.");
      }
      await yeuCauSuaChuVuonVuonApi.guiYeuCau({
        loai: "chuVuon",
        targetId: chuVuon.id,
        duLieuMoi: patch,
      });
      setModalDeXuat(null);
      setThongBaoDeXuat("Đã gửi yêu cầu sửa — admin duyệt tại Chờ duyệt CV/Vườn.");
    } catch (err) {
      setLoiDeXuat(err.message);
    } finally {
      setDangGuiDeXuat(false);
    }
  }

  if (dangTai) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <MDTypography variant="button" color="text">
            Đang tải...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!chuVuon) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <MDTypography variant="button" color="text">
            Không tìm thấy chủ vườn.
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  // 1 dòng/PO (hoá đơn) — 1 lượt thu mua nhiều loại bơ vẫn là 1 hoá đơn duy
  // nhất, không tách theo từng loại bơ nữa.
  const giaoDichList = danhSachPO.map((po) => ({
    ...po,
    tongKgPO: po.items.reduce((s, it) => s + it.khoiLuong, 0),
    tongTienPO: po.items.reduce((s, it) => s + it.thanhTien, 0),
    tenLoaiBoGop: [...new Set(po.items.map((it) => it.tenLoaiBo).filter(Boolean))].join(", "),
  }));

  const tongKg = giaoDichList.reduce((s, po) => s + po.tongKgPO, 0);
  const tongTien = giaoDichList.reduce((s, po) => s + po.tongTienPO, 0);
  const soCccd = hienThiCccd(chuVuon, vaiTro);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Chủ vườn"
          tieuDe={chuVuon.hoTen}
          moTa={`Mã ${chuVuon.maChuVuon ?? "—"} · SĐT ${chuVuon.sdt} · CCCD ${soCccd || "—"}`}
        />

        {thongBaoDeXuat && (
          <MDBox mb={2}>
            <MDAlert color="success">
              {thongBaoDeXuat}{" "}
              <MDTypography
                component={Link}
                to="/cho-duyet/chu-vuon-vuon"
                variant="caption"
                color="success"
                sx={{ textDecoration: "underline" }}
              >
                Xem yêu cầu của tôi
              </MDTypography>
            </MDAlert>
          </MDBox>
        )}

        <Card sx={{ mb: 3 }}>
          <MDBox p={{ xs: 2.5, sm: 3 }}>
            <MDBox
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              mb={2}
            >
              <MDTypography variant="h5" component="h2">
                Thông tin chủ vườn
              </MDTypography>
              {!laAdmin && (
                <MDButton variant="outlined" color="info" size="small" onClick={moDeXuatSua}>
                  <Icon sx={{ mr: 0.5 }} fontSize="small">
                    edit_note
                  </Icon>
                  Đề xuất sửa
                </MDButton>
              )}
            </MDBox>
            <Grid container spacing={2}>
              <Dong nhan="Loại chủ vườn" giaTri={NHAN_LOAI_CHU_VUON[chuVuon.loaiChuVuon]} />
              <Dong nhan="Số CCCD" giaTri={soCccd} />
              <Dong nhan="Ngày sinh" giaTri={ngayHienThi(chuVuon.ngaySinh)} />
              <Dong nhan="Ngày cấp CCCD" giaTri={ngayHienThi(chuVuon.ngayCapCccd)} />
              <Dong nhan="Nơi cấp CCCD" giaTri={chuVuon.noiCapCccd} />
              <Dong nhan="Email" giaTri={chuVuon.email} />
              <Dong
                nhan="Địa chỉ"
                giaTri={[chuVuon.diaChi, chuVuon.thon, chuVuon.xa, chuVuon.tinh]
                  .filter(Boolean)
                  .join(", ")}
              />
              <Grid item xs={12}>
                <MDBox
                  mt={1}
                  pt={1.5}
                  sx={({ palette }) => ({ borderTop: `1px solid ${palette.grey[300]}` })}
                >
                  <MDTypography variant="overline" color="text">
                    Tài khoản nhận tiền
                  </MDTypography>
                </MDBox>
              </Grid>
              <Dong nhan="Ngân hàng" giaTri={chuVuon.tenNganHang} />
              <Dong nhan="Số tài khoản" giaTri={chuVuon.soTkNganHang} />
              <Dong nhan="Chủ tài khoản" giaTri={chuVuon.chuTaiKhoan} />
              <Dong nhan="Ghi chú" giaTri={chuVuon.ghiChu} />
            </Grid>
          </MDBox>
        </Card>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Card id="tour-chu-vuon-chi-tiet-lich-su">
              <MDBox
                px={{ xs: 2.5, sm: 3 }}
                py={2}
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <MDTypography variant="h5" component="h2">
                  Lịch sử giao dịch
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {giaoDichList.length} giao dịch
                </MDTypography>
              </MDBox>

              {giaoDichList.length === 0 ? (
                <MDBox px={2.5} pb={4} pt={1} textAlign="center">
                  <Icon sx={({ palette }) => ({ color: palette.grey[400], fontSize: "2.5rem" })}>
                    receipt_long
                  </Icon>
                  <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                    Chủ vườn này chưa có giao dịch nào.
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                    Dòng đầu tiên xuất hiện ngay sau lượt thu mua đầu tiên.
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  <MDBox sx={{ overflowX: "auto" }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={oTieuDeCot}>Ngày</TableCell>
                          <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Loại bơ</TableCell>
                          <TableCell align="right" sx={oTieuDeCot}>
                            Tổng kg
                          </TableCell>
                          <TableCell align="right" sx={oTieuDeCot}>
                            Tổng tiền
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {giaoDichList.map((po) => (
                          <TableRow
                            key={po.id}
                            sx={({ palette }) => ({
                              transition: "background-color 160ms ease",
                              "&:hover": { backgroundColor: palette.grey[100] },
                            })}
                          >
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <MDTypography variant="button" fontWeight="medium" display="block">
                                {new Date(po.thoiGian).toLocaleDateString("vi-VN")}
                              </MDTypography>
                              {po.tenLoaiBoGop && (
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  display={{ xs: "block", md: "none" }}
                                  mt={0.25}
                                  lineHeight={1.4}
                                  sx={chuXuongDong}
                                >
                                  {po.tenLoaiBoGop}
                                </MDTypography>
                              )}
                            </TableCell>
                            <TableCell sx={[AN_DUOI_MD, chuXuongDong]}>
                              {po.tenLoaiBoGop || "—"}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                              {po.tongKgPO}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                              {po.tongTienPO.toLocaleString("vi-VN")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </MDBox>

                  <MDBox
                    px={{ xs: 2.5, sm: 3 }}
                    py={2}
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="flex-end"
                    gap={{ xs: 2, sm: 4 }}
                    sx={({ palette }) => ({ borderTop: `1px solid ${palette.grey[300]}` })}
                  >
                    <MDTypography variant="button" fontWeight="bold">
                      Tổng: {tongKg.toLocaleString("vi-VN")} kg
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="bold">
                      {tongTien.toLocaleString("vi-VN")} đ
                    </MDTypography>
                  </MDBox>
                </>
              )}
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card id="tour-chu-vuon-chi-tiet-qr">
              <MDBox p={{ xs: 2.5, sm: 3 }}>
                <NhanDau>In dán</NhanDau>
                <MDTypography variant="h5" component="h2">
                  Mã QR chủ vườn
                </MDTypography>
                <MDTypography variant="button" fontWeight="regular" color="text" display="block">
                  Nhân viên quét mã này ở bước 1 để mở đúng hồ sơ chủ vườn, không cần gõ CCCD.
                </MDTypography>
                <MDBox mt={2.5} display="flex" flexDirection="column" alignItems="center">
                  {chuVuon.maChuVuon ? (
                    <QrCodeImage value={chuVuon.maChuVuon} />
                  ) : (
                    <MDTypography variant="button" color="text">
                      Chủ vườn này chưa có mã.
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={!!modalDeXuat}
        onClose={() => setModalDeXuat(null)}
        fullWidth
        maxWidth="md"
        fullScreen={fullManHinh}
        PaperProps={oDialogPaperFormDai}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 2, flexShrink: 0, ...chuXuongDong }}>
          <NhanDau>Đề xuất sửa</NhanDau>
          <MDTypography variant="h5" component="h2">
            Chủ vườn
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block" mt={0.5}>
            Thay đổi sẽ gửi admin duyệt — không cập nhật trực tiếp hồ sơ.
          </MDTypography>
        </DialogTitle>
        {modalDeXuat && (
          <MDBox component="form" onSubmit={guiDeXuatChuVuon} sx={oDialogFormFlex}>
            <DialogContent sx={oDialogContentFormDai}>
              {loiDeXuat && (
                <MDBox mb={2}>
                  <MDAlert color="error">{loiDeXuat}</MDAlert>
                </MDBox>
              )}
              <Grid container spacing={2}>
                <FormChuVuon
                  value={modalDeXuat}
                  onChange={setModalDeXuat}
                  batBuocCaNhan={false}
                  khoaCccd={!!soCccdDayDuTuHoSo(modalDeXuat)}
                />
              </Grid>
            </DialogContent>
            <DialogActions sx={oDialogActionsFormDai}>
              <MDButton variant="outlined" color="secondary" onClick={() => setModalDeXuat(null)}>
                Huỷ
              </MDButton>
              <MDButton type="submit" variant="gradient" color="info" disabled={dangGuiDeXuat}>
                {dangGuiDeXuat ? "Đang gửi..." : "Gửi duyệt"}
              </MDButton>
            </DialogActions>
          </MDBox>
        )}
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default ChiTietChuVuon;
